import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { toast } from "react-toastify";
import { LoginAPI, consumeAuthLogoutMessage } from "../api";
import AuthLayout from "./AuthLayout";
import AuthTextField from "./AuthTextField";
import AuthPrimaryButton from "./AuthPrimaryButton";
import { authActionsSx, authFormStackSx, authFormTitleSx } from "./authStyles";

var emailSchema = Yup.object({
  email: Yup.string()
    .trim()
    .required("Email is required")
    .email("Enter a valid email address"),
});

var ROLE_LOOKUP_DEBOUNCE_MS = 500;

function Login() {
  const navigate = useNavigate();
  const [role, setRole] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(function () {
    var logoutMessage = consumeAuthLogoutMessage();
    if (logoutMessage) {
      toast.error(logoutMessage, {
        hideProgressBar: true,
        closeOnClick: true,
      });
    }
  }, []);

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: emailSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: function (values) {
      var email = values.email.trim();
      setSubmitting(true);
      LoginAPI.login({ email: email })
        .then(function (response) {
          setSubmitting(false);
          if (response.success) {
            if (response.data.isAdmin || response.data.isSuperAdmin) {
              navigate("/AdminLogin", {
                state: {
                  email: email,
                  role:
                    role ||
                    (response.data.isAdmin ? "Admin" : "SuperAdmin"),
                },
              });
            } else {
              toast.success(response.message, {
                hideProgressBar: true,
                closeOnClick: true,
              });
              navigate("/otp-verify", { state: { email: email } });
            }
          } else {
            formik.setFieldError(
              "email",
              response.message || "Unable to login"
            );
            formik.setFieldTouched("email", true, false);
          }
        })
        .catch(function (err) {
          setSubmitting(false);
          var apiMessage =
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
              ? err.response.data.message
              : "Unable to login";
          formik.setFieldError("email", apiMessage);
          formik.setFieldTouched("email", true, false);
          console.log(err);
        });
    },
  });

  var typedEmail = formik.values.email.trim();

  useEffect(
    function () {
      if (!Yup.string().email().required().isValidSync(typedEmail)) {
        setRole("");
        return undefined;
      }
      var cancelled = false;
      var timer = setTimeout(function () {
        LoginAPI.role({ email: typedEmail })
          .then(function (response) {
            if (!cancelled) setRole(response.data);
          })
          .catch(function () {
            if (!cancelled) setRole("");
          });
      }, ROLE_LOOKUP_DEBOUNCE_MS);
      return function () {
        cancelled = true;
        clearTimeout(timer);
      };
    },
    [typedEmail]
  );

  var emailTouched = formik.touched.email;
  var emailError =
    emailTouched && formik.errors.email ? formik.errors.email : "";

  var submitLabel =
    role === "Admin" || role === "SuperAdmin" ? "Enter password" : "Get OTP";

  var canSubmit =
    !submitting &&
    Boolean(formik.values.email) &&
    !formik.errors.email;

  return (
    <AuthLayout>
      <Typography component="h1" sx={authFormTitleSx}>
        LOGIN
      </Typography>

      <Box
        component="form"
        noValidate
        onSubmit={formik.handleSubmit}
        sx={authFormStackSx}
      >
        <AuthTextField
          id="email"
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          autoFocus
          placeholder="Enter Email Address"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(emailError)}
          helperText={emailError}
        />

        <Box sx={authActionsSx}>
          <AuthPrimaryButton type="submit" disabled={!canSubmit} loading={submitting}>
            {submitLabel}
          </AuthPrimaryButton>
        </Box>
      </Box>
    </AuthLayout>
  );
}

export default Login;
